// Copyright (c) Ping. All rights reserved.
//
// Pure logic for the in-chat realtime games (Coin Flip, Dice Roll, Rock
// Paper Scissors). The wire model is dead simple and fully peer-to-peer:
// every game is `create` + exactly two `move`s (seat 0 = creator, seat 1 =
// opponent), and BOTH clients compute the identical winner locally from
// the two moves — no server referee, no extra outcome round-trip needed.
//
// Move `payload` rides the `com.ping.game_move` envelope as a string
// (the protocol's "hex" field is read verbatim); we put a tiny JSON blob
// in it. Seat == `move_idx` (creator always emits move 0, opponent move 1).

import type { GameSession } from '../../sync/useGameSessions';

export type CoinSide = 'heads' | 'tails';
export type RpsChoice = 'rock' | 'paper' | 'scissors';

/** Seat that won: 0 = creator, 1 = opponent, -1 = draw / undecided. */
export type WinnerSeat = 0 | 1 | -1;

// ── move payload codecs ────────────────────────────────────────────────

interface CoinPickPayload {
  readonly side: CoinSide;
}
interface CoinFlipPayload {
  readonly result: CoinSide;
}
interface DicePayload {
  readonly value: number; // 1..6
}
interface RpsPayload {
  readonly choice: RpsChoice;
}

export function encodeMove(data: unknown): string {
  return JSON.stringify(data);
}

function decode<T>(payload: string | undefined): T | null {
  if (payload === undefined || payload.length === 0) return null;
  try {
    return JSON.parse(payload) as T;
  } catch {
    return null;
  }
}

// ── randomness (each client rolls its OWN move) ─────────────────────────

export function randomCoin(): CoinSide {
  return pickRandomInt(2) === 0 ? 'heads' : 'tails';
}
export function randomDie(): number {
  return pickRandomInt(6) + 1;
}
function pickRandomInt(maxExclusive: number): number {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return (buf[0] ?? 0) % maxExclusive;
  }
  // Deterministic fallback only (crypto always present on web/macOS).
  return 0;
}

// ── per-seat move lookup ────────────────────────────────────────────────

export function moveAtSeat(session: GameSession, seat: 0 | 1): string | undefined {
  return session.moves.find((m) => m.moveIdx === seat)?.payload;
}

// ── Coin Flip ───────────────────────────────────────────────────────────
//   move 0 (creator): { side }      — the side the creator called
//   move 1 (opponent): { result }   — the opponent flips; their client
//                                      generates the random result.
//   winner = result === side ? creator(0) : opponent(1)

export interface CoinState {
  readonly side: CoinSide | null;
  readonly result: CoinSide | null;
  readonly resolved: boolean;
  readonly winnerSeat: WinnerSeat;
}

export function readCoinState(session: GameSession): CoinState {
  const side = decode<CoinPickPayload>(moveAtSeat(session, 0))?.side ?? null;
  const result = decode<CoinFlipPayload>(moveAtSeat(session, 1))?.result ?? null;
  const resolved = side !== null && result !== null;
  return {
    side,
    result,
    resolved,
    winnerSeat: resolved ? (result === side ? 0 : 1) : -1,
  };
}

export function encodeCoinPick(side: CoinSide): string {
  return encodeMove({ side } satisfies CoinPickPayload);
}
export function encodeCoinFlip(result: CoinSide): string {
  return encodeMove({ result } satisfies CoinFlipPayload);
}

// ── Dice Roll ───────────────────────────────────────────────────────────
//   move 0 (creator): { value }, move 1 (opponent): { value }
//   winner = higher value (draw on tie)

export interface DiceState {
  readonly creatorValue: number | null;
  readonly opponentValue: number | null;
  readonly resolved: boolean;
  readonly winnerSeat: WinnerSeat;
}

export function readDiceState(session: GameSession): DiceState {
  const creatorValue = decode<DicePayload>(moveAtSeat(session, 0))?.value ?? null;
  const opponentValue = decode<DicePayload>(moveAtSeat(session, 1))?.value ?? null;
  const resolved = creatorValue !== null && opponentValue !== null;
  let winnerSeat: WinnerSeat = -1;
  if (resolved) {
    winnerSeat = creatorValue! > opponentValue! ? 0 : opponentValue! > creatorValue! ? 1 : -1;
  }
  return { creatorValue, opponentValue, resolved, winnerSeat };
}

export function encodeDiceRoll(value: number): string {
  return encodeMove({ value } satisfies DicePayload);
}

// ── Rock Paper Scissors ─────────────────────────────────────────────────
//   move 0 (creator): { choice }, move 1 (opponent): { choice }
//   each client hides the opponent's choice until BOTH have moved.

export interface RpsState {
  readonly creatorChoice: RpsChoice | null;
  readonly opponentChoice: RpsChoice | null;
  readonly resolved: boolean;
  readonly winnerSeat: WinnerSeat;
}

export function readRpsState(session: GameSession): RpsState {
  const creatorChoice = decode<RpsPayload>(moveAtSeat(session, 0))?.choice ?? null;
  const opponentChoice = decode<RpsPayload>(moveAtSeat(session, 1))?.choice ?? null;
  const resolved = creatorChoice !== null && opponentChoice !== null;
  return {
    creatorChoice,
    opponentChoice,
    resolved,
    winnerSeat: resolved ? rpsWinner(creatorChoice!, opponentChoice!) : -1,
  };
}

export function encodeRpsChoice(choice: RpsChoice): string {
  return encodeMove({ choice } satisfies RpsPayload);
}

function rpsWinner(a: RpsChoice, b: RpsChoice): WinnerSeat {
  if (a === b) return -1;
  const beats: Record<RpsChoice, RpsChoice> = {
    rock: 'scissors',
    paper: 'rock',
    scissors: 'paper',
  };
  return beats[a] === b ? 0 : 1;
}

// ── shared seat helpers ─────────────────────────────────────────────────

/** My seat in a session, derived from whether I created it. */
export function mySeat(isCreator: boolean): 0 | 1 {
  return isCreator ? 0 : 1;
}

/** Has the given seat already moved? */
export function hasMoved(session: GameSession, seat: 0 | 1): boolean {
  return session.moves.some((m) => m.moveIdx === seat);
}
